import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import actions from "actions";
import { useGlobalToast } from "components/GlobalToastProvider";

interface UseNewAuthorizationParams {
  clientId: string;
  availableInsuranceCodes: any[];
  overlapError: boolean;
  onOverlapDetected: () => void;
  checkForOverlaps: (params: any) => void;
  resetOverlap: () => void;
}

const useNewAuthorization = ({
  clientId,
  availableInsuranceCodes,
  overlapError,
  onOverlapDetected,
  checkForOverlaps,
  resetOverlap,
}: UseNewAuthorizationParams) => {
  const dispatch = useDispatch();
  const { showToast } = useGlobalToast();

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [authType, setAuthType] = useState("");
  const [subtype, setSubtype] = useState("");
  const [clinicianId, setClinicianId] = useState("");
  const [pendingAuth, setPendingAuth] = useState(false);
  const [approvedUnits, setApprovedUnits] = useState([
    { code: "", units: 0, hours: 0, codeId: "" },
  ]);

  useEffect(() => {
    if (open) {
      checkForOverlaps({
        startDate,
        endDate,
        authType,
        approvedUnits,
      });
    }
  }, [startDate, endDate, authType, approvedUnits, open, checkForOverlaps]);

  const toggle = useCallback(() => {
    resetOverlap();
    if (open) {
      setCode("");
      setStartDate(null);
      setEndDate(null);
      setAuthType("");
      setSubtype("");
      setClinicianId("");
      setPendingAuth(false);
      setApprovedUnits([{ code: "", units: 0, hours: 0, codeId: "" }]);
    }
    setOpen(!open);
  }, [open, resetOverlap]);

  const handleChange = (name: string) => (e: any) => {
    const { checked, type, value } = e.target;
    const val = type === "checkbox" ? checked : value;
    switch (name) {
      case "newAuthorizationCode": setCode(val); break;
      case "newAuthorizationType": setAuthType(val); break;
      case "newAuthorizationSubtype": setSubtype(val); break;
      case "newAuthorizationClinicianId": setClinicianId(val); break;
      case "newPendingAuth": setPendingAuth(val); break;
    }
  };

  const handleDateChange = (name: string, date: Date | null) => {
    switch (name) {
      case "newAuthorizationStartDate": setStartDate(date); break;
      case "newAuthorizationEndDate": setEndDate(date); break;
    }
  };

  const addApprovedUnit = () => {
    setApprovedUnits([...approvedUnits, { code: "", units: 0, hours: 0, codeId: "" }]);
  };

  const removeApprovedUnit = (index: number) => {
    const units = [...approvedUnits];
    units.splice(index, 1);
    setApprovedUnits(units);
  };

  const changeApprovedUnitCode = (index: number, e: any) => {
    const units = [...approvedUnits];
    units[index].code = e.target.value;
    const codeId = availableInsuranceCodes.find(
      (c: any) => c.insurance_code === e.target.value
    )?.id;
    units[index].codeId = codeId;
    setApprovedUnits(units);
  };

  const changeApprovedUnitAmount = (index: number, e: any) => {
    const units = [...approvedUnits];
    units[index].units = e.target.value;
    units[index].hours = e.target.value / 4;
    setApprovedUnits(units);
  };

  const save = () => {
    if (overlapError) {
      onOverlapDetected();
    } else {
      submit();
    }
  };

  const submit = (isSecondaryAuth?: boolean) => {
    const secondaryAuth =
      isSecondaryAuth !== undefined ? isSecondaryAuth : authType === "secondary_auth";

    dispatch(
      actions.createAuthorizationCode({
        isSecondaryAuth: secondaryAuth,
        authorizationCode: code,
        startDate,
        endDate,
        clientId: parseInt(clientId),
        approvedUnits: secondaryAuth ? null : approvedUnits,
        authorizationType: authType,
        authorizationSubtype: subtype,
        clinicianId,
        pendingAuth,
      })
    );

    dispatch(actions.getAuthorizationCodes(parseInt(clientId)));
    showToast({ message: "Authorization added successfully" });
    toggle();
  };

  return {
    open,
    code,
    startDate,
    endDate,
    authType,
    subtype,
    clinicianId,
    pendingAuth,
    approvedUnits,
    toggle,
    handleChange,
    handleDateChange,
    addApprovedUnit,
    removeApprovedUnit,
    changeApprovedUnitCode,
    changeApprovedUnitAmount,
    save,
    submit,
  };
};

export default useNewAuthorization;
